// frontend/src/pages/PrivacyPage.jsx
import React from "react";
import { Link } from "react-router-dom";
import Seo from "../marketing/Seo";
import { LEGAL_LAST_UPDATED } from "../marketing/data/legal";

/**
 * PrivacyPage — GDPR privacy policy. Rendered inside the public LegalLayout
 * (sidebar + marketing shell) at /legal/privacy; /privacy redirects here.
 *
 * Content is inline JSX (no backend call). The data-retention table reflects
 * QRFlow's actual practice: the raw client IP is anonymised at the moment of
 * scan and never stored (see P20-G0b), so there is no "anonymise after N days"
 * step — stating one would be a false claim in a legal document.
 */
function PrivacyPage() {
  return (
    <article>
      <Seo
        title="Privacy Policy"
        description="How QRFlow collects, uses, retains, and protects your data — GDPR-first, never storing raw IPs."
        path="/legal/privacy"
      />
      <p className="text-sm text-gray-500">Last updated: {LEGAL_LAST_UPDATED}</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">Privacy Policy</h1>
      <p className="mt-3 text-gray-600">
        How QRFlow collects, uses, retains, and protects your data.
      </p>

      <section className="mt-10">
        <h2 className="text-xl font-semibold text-gray-900">Data We Collect</h2>
        <p className="mt-3 leading-7 text-gray-700">
          Account data you provide (email and display name), the QR codes you create, and
          scan events generated when your codes are scanned. Scan events include a coarse,
          anonymised location and device type. We do <strong>not</strong> store the raw IP
          address of a scan — it is anonymised the moment a scan is recorded. If you use the
          AI features, we also process whatever <strong>you choose to type into them</strong>{" "}
          (your messages and any context they include) — see below.
        </p>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900">How We Use Data</h2>
        <p className="mt-3 leading-7 text-gray-700">
          To provide the QR code service, show you scan analytics, secure your account, and
          (only with your consent) to improve your experience. We do not sell your personal
          data.
        </p>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900">
          AI Features &amp; Third-Party AI Providers
        </h2>
        <p className="mt-3 leading-7 text-gray-700">
          The Service includes optional AI features (the in-app assistant and support
          agents). What you submit to them — <strong>your messages and any context they
          include</strong> — is processed by an AI model to generate a response. Depending on
          how the Service is configured, that model may be operated by a{" "}
          <strong>third-party AI provider such as Anthropic, OpenAI, or Google</strong>. When
          a cloud model is used, the content you submit is{" "}
          <strong>sent to and processed by that provider on their own systems, outside
          QRFlow&rsquo;s infrastructure and control</strong>, under their terms and privacy
          practices, and the provider may be located outside the EEA/UK (an international
          transfer made under that provider&rsquo;s safeguards, such as Standard Contractual
          Clauses).
        </p>
        <p className="mt-3 leading-7 text-gray-700">
          This content reaches the provider <strong>because you send it</strong>. You decide
          what to submit, and you should <strong>not enter passwords, payment-card data,
          special-category or other sensitive personal data, or confidential information</strong>{" "}
          into the AI features. QRFlow is not responsible for a third-party AI provider&rsquo;s
          own processing, retention, or use of what you submit. If you do not want your
          content processed by a third-party AI provider, do not use the AI features. See our{" "}
          <Link to="/legal/terms" className="font-medium text-brand-700 hover:text-brand-800">
            Terms
          </Link>{" "}
          (§7) and{" "}
          <Link
            to="/legal/subprocessors"
            className="font-medium text-brand-700 hover:text-brand-800"
          >
            Subprocessors
          </Link>
          .
        </p>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900">Data Retention</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-gray-300 text-gray-900">
                <th className="py-2 pr-4 font-semibold">Data</th>
                <th className="py-2 font-semibold">Retention</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr className="border-b border-gray-100">
                <td className="py-2 pr-4">IP addresses</td>
                <td className="py-2">
                  Anonymised at the moment of scan (last octet zeroed); the full IP address
                  is never stored.
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 pr-4">Scan events</td>
                <td className="py-2">
                  Retained for the analytics window of your plan, then automatically pruned.
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 pr-4">Account data</td>
                <td className="py-2">Deleted within 30 days of an erasure request.</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 pr-4">AI feature submissions</td>
                <td className="py-2">
                  Processed to generate a response; chat transcripts are retained for our
                  retention window then redacted/pruned. Content sent to a third-party AI
                  provider is subject to that provider&rsquo;s own retention, outside our
                  control.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900">Your Rights</h2>
        <p className="mt-3 text-gray-700">Under the GDPR and similar laws you have the right to:</p>
        <ul className="mt-3 list-disc space-y-1 pl-6 text-gray-700">
          <li>
            <strong>Access</strong> — request a copy of the personal data we hold about you
            (available as a self-service export).
          </li>
          <li>
            <strong>Erasure</strong> — request deletion of your account and personal data
            (&ldquo;right to be forgotten&rdquo;).
          </li>
          <li>
            <strong>Portability</strong> — receive your data in a portable, machine-readable
            format.
          </li>
          <li>
            <strong>Rectification</strong> — correct inaccurate personal data.
          </li>
        </ul>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900">Cookie Policy</h2>
        <p className="mt-3 leading-7 text-gray-700">
          QRFlow uses essential cookies for authentication and optional analytics cookies to
          improve your experience. You can change your choice at any time via the
          &ldquo;Cookie Preferences&rdquo; link in the footer. See our{" "}
          <Link to="/legal/cookies" className="font-medium text-brand-700 hover:text-brand-800">
            Cookie Policy
          </Link>{" "}
          for details.
        </p>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900">Contact</h2>
        <p className="mt-3 leading-7 text-gray-700">
          Questions about your privacy? Contact us at{" "}
          <a
            href="mailto:support@qrflow.ioanna.ro"
            className="font-medium text-brand-700 hover:text-brand-800"
          >
            support@qrflow.ioanna.ro
          </a>
          .
        </p>
      </section>

      <div className="mt-12 border-t border-gray-200 pt-6">
        <Link to="/legal" className="text-sm font-medium text-brand-700 hover:text-brand-800">
          ← Back to legal centre
        </Link>
      </div>
    </article>
  );
}

export default PrivacyPage;
